import {
  AA_SECTION_IDS,
  aaQuestions,
  aaSectionName,
  type AaFieldType,
} from '@/karehero-app/explore/tell-us-once/aaQuestions';

// Shared mock data for the care recipient control panel. One care circle
// (Margarette Qualley), used by every tab so cross-links line up: actions can
// reference calls, documents can reference uploaders, and so on.

export type Carer = {
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
  primary?: boolean;
};

export const CARE_CIRCLE = {
  recipient: 'Margarette Qualley',
  recipientFirstName: 'Margarette',
  age: 64,
  careStage: 'In residential care',
  funding: 'Self-funded',
  conditions: ['Dementia', 'Reduced mobility', "Alzheimer's"],
  // The circle this one is linked to (a partner's), and how they relate.
  linkedCircle: 'Pedro Qualley',
  linkedRelationship: 'Spouse',
  linkedNote: 'shares Sophie as primary carer',
  carers: [
    {
      name: 'Sophie Qualley',
      relationship: 'daughter',
      phone: '07748557273',
      email: 'soph.qualleyy@email.com',
      primary: true,
    },
    { name: 'Harry Qualley', relationship: 'son', phone: '07748557273' },
  ] as Carer[],
};

// ─── the intake, when there has been one ─────────────────────────────────────

/**
 * What a Tell Us Once intake leaves behind on this circle.
 *
 * The read-back is the family's own words, so it has to name this family: the
 * member-side intake fixtures are a different household (Margaret Doyle), and
 * only the counts are shared. OverviewTab derives those from the member's own
 * profile content, which is nameless and cannot drift.
 */
export const INTAKE = {
  when: '4 Sep',
  where: 'in the app',
  how: ['Talked', 'Typed', 'Photographed'],
  by: 'Sophie Qualley',
  // The three lines the family confirmed at the end of the intake.
  readBack: [
    'Sophie is caring for her mum, Margarette, around 40 hours a week, and her brother Harry helps.',
    'Margarette has dementia and reduced mobility, walks with a frame, and needs help washing, dressing, eating and through the night.',
    'Sophie asked for help with money Margarette might be owed, and with care at home.',
  ],
  declined:
    'Sophie declined AI processing, so nothing here was read from what she sent. Everything on this circle came from a form or a call.',
};

// ─── case managers (KareHero staff) ──────────────────────────────────────────

// The KareHero care managers who can own a care circle. Distinct from the
// carers (the family) — this is who's responsible on our side.
// A name and nothing else: we don't hold a job role for our own staff, so a
// seniority line under the name was a fixture inventing a field.
export type CaseManager = { name: string };

export const CASE_MANAGERS: CaseManager[] = [
  { name: 'Priya Patel' },
  { name: 'Daniel Okafor' },
  { name: 'Fiona Grant' },
  { name: 'Marcus Reid' },
];

// Who currently owns this circle. undefined = unassigned.
export const ASSIGNED_CASE_MANAGER: string | undefined = 'Priya Patel';

// CAMs watching this circle (drawn from the same staff pool). Watchers get the
// circle's notifications too, but opening them doesn't mark anything read for
// the case manager — watching is read-only follow, not ownership.
export const INITIAL_WATCHERS: string[] = ['Daniel Okafor'];

// Humanised onboarding answers, question copy lifted verbatim from the
// app-side onboarding (karehero-app/Onboarding.tsx, carer path). An absent
// answer renders as 'Not answered' — CAMs need to see what's missing.
export const ONBOARDING_ANSWERS: { question: string; answer?: string }[] = [
  {
    question: 'Who are you caring for?',
    answer: 'My mum, Margarette. She’s 64 and lives on her own in Edinburgh.',
  },
  {
    question: 'Does Margarette have any diagnosed conditions?',
    answer: 'Dementia · Alzheimers · Mobility',
  },
  {
    question: 'Is Margarette on any regular medications?',
    answer: 'Donepezil, 10mg daily',
  },
  {
    question: 'Is there a care arrangement currently in place?',
    answer: 'No, family covers everything right now',
  },
  {
    question: 'Has something recently changed?',
    answer: 'Yes',
  },
  {
    question: 'What happened?',
    answer:
      'A fall at home last month. She wasn’t hurt badly but it shook everyone.',
  },
  {
    question: 'How long have you been supporting Margarette?',
    answer: 'Over a year',
  },
  { question: 'What do you need help with?' },
];

// ─── actions ─────────────────────────────────────────────────────────────────

export const APP_SECTIONS = [
  'Shortlist',
  'Care assessment',
  'Care profile',
  'Funding guide',
] as const;

export const FREQUENCIES = ['Every day', 'Every week', 'Every month'] as const;
export type Frequency = (typeof FREQUENCIES)[number];

export type ActionItem = {
  id: string;
  title: string;
  note?: string;
  // Carer name, or undefined for unassigned.
  assignee?: string;
  due?: string; // e.g. 'Thu 28 May'
  time?: string; // e.g. '14:00'
  // Seeded flag — the prototype has no real clock.
  overdue?: boolean;
  repeats?: Frequency;
  done?: boolean;
  // Deeplink into the member app.
  link?: string;
  customUrl?: string;
  linkedCallId?: string;
  // Where the action sits relative to its linked call.
  callPhase?: 'before' | 'after';
  createdBy: string; // 'you' or a CAM name
};

// A deliberate spread of action shapes so the drawer's view mode can be seen
// varying: dated vs recurring vs no-date, assigned vs unassigned, self- vs
// CAM-created, with/without a note, with/without a linked call, and done.
export const SEED_ACTIONS: ActionItem[] = [
  // Overdue one-off — the richest view: a note AND a linked call, assigned.
  {
    id: 'a1',
    title: 'Upload 3 months of bank statements',
    note: 'We need three months of recent statements to complete the funding picture. Please upload these as a PDF. I’ve attached an example of what they should look like.',
    assignee: 'Sophie Qualley',
    due: 'Thu 28 May',
    overdue: true,
    linkedCallId: 'c2',
    callPhase: 'before',
    createdBy: 'you',
  },
  // Recurring, set by a colleague — recurring schedule line + a note from
  // another case manager.
  {
    id: 'a2',
    title: 'Bath assist',
    note: 'Help Margarette with her evening bath. She prefers the shower seat and the room warm beforehand.',
    repeats: 'Every day',
    time: '14:00',
    assignee: 'Harry Qualley',
    createdBy: 'Priya Patel',
  },
  // Recurring, self-created — same recurring shape as a2 but created by you and
  // weekly, with no note.
  {
    id: 'a8',
    title: 'Weekly welfare check-in',
    repeats: 'Every week',
    time: '11:00',
    assignee: 'Sophie Qualley',
    createdBy: 'you',
  },
  // Dated with a linked call, no note.
  {
    id: 'a3',
    title: 'Upload proof of address',
    assignee: 'Sophie Qualley',
    due: 'Fri 13 Jun',
    linkedCallId: 'c2',
    callPhase: 'after',
    createdBy: 'you',
  },
  // Plain dated action — assigned, no note or linked call.
  {
    id: 'a4',
    title: 'Send pension information',
    assignee: 'Sophie Qualley',
    due: 'Mon 15 Jun',
    createdBy: 'you',
  },
  // Dated, deep-links into an app section.
  {
    id: 'a5',
    title: 'Review your shortlist',
    assignee: 'Sophie Qualley',
    due: 'Sat 14 Jun',
    link: 'Shortlist',
    createdBy: 'you',
  },
  // No date and unassigned — the sparsest view.
  {
    id: 'a6',
    title: 'Read the funding guide',
    link: 'Funding guide',
    createdBy: 'you',
  },
  // Completed — shows the done state (Mark as not done) with a note.
  {
    id: 'a7',
    title: 'Complete the care assessment',
    note: 'Completed with Sophie on the assessment call.',
    assignee: 'Sophie Qualley',
    due: 'Sat 7 Jun',
    link: 'Care assessment',
    done: true,
    createdBy: 'you',
  },
];

// ─── calls ───────────────────────────────────────────────────────────────────

export type Call = {
  id: string;
  title: string;
  withWho: string;
  date: string; // e.g. 'Sat 7 Jun'
  time: string; // e.g. '16:00'
  duration?: string;
  type?: string;
  notes?: string;
  upcoming?: boolean;
  // File names linked to the call, shown in the detail drawer.
  documents?: string[];
};

export const SEED_CALLS: Call[] = [
  {
    id: 'c3',
    title: 'Funding call',
    withWho: 'Sophie',
    date: 'Wed 17 Jun',
    time: '10:00',
    type: 'Video call',
    upcoming: true,
  },
  {
    id: 'c2',
    title: 'Care assessment',
    withWho: 'Sophie',
    date: 'Sat 7 Jun',
    time: '16:00',
    duration: '45 min',
    type: 'Video call',
    notes:
      'Walked through the care assessment with Sophie. Margarette needs residential care with dementia support, ideally close to the family in Edinburgh. Funding picture is incomplete — bank statements requested.',
    documents: ['care_assessment_summary.pdf', 'bank_statement_march.pdf'],
  },
  {
    id: 'c1',
    title: 'Initial call',
    withWho: 'Sophie',
    date: 'Mon 1 Jun',
    time: '10:00',
    duration: '30 min',
    type: 'Phone call',
    notes:
      'First chat with Sophie about Margarette’s situation. A fall last month prompted the search. Family wants to understand the options before anything is decided.',
  },
];

// ─── documents ───────────────────────────────────────────────────────────────

export const DOCUMENT_TAGS = [
  'Find care',
  'Power of attorney',
  'Funding',
] as const;

export type DocumentItem = {
  id: string;
  name: string;
  size: string;
  uploadedBy: string;
  date: string;
  tags: string[];
  note?: string;
};

export const SEED_DOCUMENTS: DocumentItem[] = [
  {
    id: 'd1',
    name: 'care_assessment_summary.pdf',
    size: '120kb',
    uploadedBy: 'You',
    date: '7 Jun 2026',
    tags: ['Find care'],
  },
  {
    id: 'd2',
    name: 'bank_statement_march.pdf',
    size: '96kb',
    uploadedBy: 'Sophie',
    date: '9 Jun 2026',
    tags: ['Funding'],
  },
  {
    id: 'd3',
    name: 'poa_certificate.pdf',
    size: '210kb',
    uploadedBy: 'Sophie',
    date: '2 Jun 2026',
    tags: ['Power of attorney'],
  },
  {
    id: 'd4',
    name: 'oakwood_brochure.pdf',
    size: '1.2mb',
    uploadedBy: 'You',
    date: '10 Jun 2026',
    tags: ['Find care'],
    note: 'Brochure from the Oakwood visit.',
  },
];

// ─── care-circle forms ───────────────────────────────────────────────────────

// The forms the care circle completes in their app (the real app's care-profile
// categories: care assessment, financial status, benefits eligibility,
// attendance allowance). The CAM reviews the family's answers here and can edit
// any of them — each form is sections of questions with their current answer.
export type FormStatus =
  'Not started' | 'In progress' | 'Submitted' | 'Reviewed';

// How an answer is captured — drives the CAM's inline editor. Mirrors the
// member-app financial-assessment field kinds, trimmed to what these forms use.
export type FormFieldKind =
  'text' | 'select' | 'choice' | 'currency' | 'yesno' | 'date' | 'multi';

export type FormOption = { value: string; label: string };

export type FormQuestion = {
  id: string;
  question: string;
  kind: FormFieldKind;
  // Current answer. '' = not answered yet. For yesno: 'yes' | 'no' | 'unsure'.
  answer: string;
  options?: FormOption[];
  /**
   * Only asked when something above it was answered a particular way, and the
   * sentence saying which.
   *
   * Attendance Allowance is 227 questions and only 64 of them are asked of
   * everybody. The rest hang off an answer, so they are collapsed behind their
   * section's count rather than listed as though a care expert had 227 things
   * to fill in.
   */
  conditional?: boolean;
  askedWhen?: string;
};

export type FormSection = { title: string; questions: FormQuestion[] };

export type FormItem = {
  id: string;
  name: string;
  description: string;
  icon: string;
  status: FormStatus;
  /**
   * The one that is not like the others, drawn coral and sorted to the top.
   *
   * Benefits eligibility is what says which claims are worth doing at all, so
   * it is the one to start with rather than one of four things to get through.
   * The member app draws the same distinction the same way. See ClaimRow's
   * `lead` and FILLED.lead.
   */
  lead?: boolean;
  // Who's completing it, and when it last moved.
  owner?: string;
  updated?: string;
  sections: FormSection[];
};

// Chip colour per status (MUI palette keys).
export const FORM_STATUS_COLOR: Record<
  FormStatus,
  'default' | 'warning' | 'success' | 'info'
> = {
  'Not started': 'default',
  'In progress': 'warning',
  Submitted: 'success',
  Reviewed: 'info',
};

const YESNO: FormOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' },
];

/** How a schema field is answered, in the shape this tab's rows take. */
const AA_KIND: Record<AaFieldType, FormFieldKind> = {
  text: 'text',
  number: 'text',
  'question-table': 'text',
  // The schema's `address` is one field in the PDF, so it stays one here until
  // somebody decides what the parts are. `none` is a heading with no answer.
  address: 'text',
  none: 'text',
  date: 'date',
  select: 'select',
  'slider-select': 'select',
  boolean: 'yesno',
  'multi-select-box': 'multi',
};

/**
 * Attendance Allowance, every question of it.
 *
 * Built from KareHero's own normalised schema rather than written out here, so
 * the form a care expert reviews is the form we have to satisfy. It used to be
 * three questions under two invented headings, which is a reasonable sketch of
 * a form and not this one: the real thing is 227 questions across fourteen
 * sections, and 93 of them are in the day-time care needs section alone.
 *
 * Only 64 are asked of everybody. The rest hang off an answer above them, and
 * carry the sentence saying which, so the drawer can collapse them behind
 * their section's count instead of listing 227 things to fill in.
 */
const aaSections = (): FormSection[] =>
  AA_SECTION_IDS.map((section) => ({
    title: aaSectionName(section),
    questions: aaQuestions(section).map((q) => ({
      id: q.id,
      question: q.label,
      kind: AA_KIND[q.type] ?? 'text',
      answer: '',
      ...(q.options.length > 0
        ? { options: q.options.map((o) => ({ value: o, label: o })) }
        : q.type === 'boolean'
          ? { options: YESNO }
          : {}),
      ...(q.depth > 0
        ? {
            conditional: true,
            ...(q.revealedBy ? { askedWhen: q.revealedBy } : {}),
          }
        : {}),
    })),
    // Three of the schema's files are declarations and guidance with no
    // questions in them. A section with nothing to answer is not a section.
  })).filter((section) => section.questions.length > 0);

export const SEED_FORMS: FormItem[] = [
  {
    id: 'care-assessment',
    name: 'Care assessment',
    description: "Margarette's care needs: daily living, health and support.",
    icon: 'heart',
    status: 'In progress',
    owner: 'Sophie',
    updated: '9 Jun 2026',
    sections: [
      {
        title: 'Daily living',
        questions: [
          {
            id: 'washing',
            question: 'Does Margarette need help washing or bathing?',
            kind: 'choice',
            answer: 'some',
            options: [
              { value: 'independent', label: 'Manages on her own' },
              { value: 'some', label: 'Needs some help' },
              { value: 'full', label: 'Needs full help' },
            ],
          },
          {
            id: 'meals',
            question: 'Who prepares her meals?',
            kind: 'choice',
            answer: 'family',
            options: [
              { value: 'herself', label: 'Herself' },
              { value: 'family', label: 'Family' },
              { value: 'carer', label: 'A paid carer' },
            ],
          },
        ],
      },
      {
        title: 'Health & mobility',
        questions: [
          {
            id: 'mobility',
            question: 'How does she get around indoors?',
            kind: 'choice',
            answer: 'aid',
            options: [
              { value: 'unaided', label: 'Unaided' },
              { value: 'aid', label: 'With a stick or frame' },
              { value: 'wheelchair', label: 'Wheelchair' },
            ],
          },
          {
            id: 'falls',
            question: 'Has she had a fall in the last 6 months?',
            kind: 'yesno',
            answer: 'yes',
            options: YESNO,
          },
          {
            id: 'medication',
            question: 'Does she manage her own medication?',
            kind: 'yesno',
            answer: '',
            options: YESNO,
          },
        ],
      },
      {
        title: 'Support network',
        questions: [
          {
            id: 'mainCarer',
            question: 'Who is the main carer?',
            kind: 'text',
            answer: 'Sophie (daughter)',
          },
          {
            id: 'hours',
            question: 'Roughly how many hours of care a week?',
            kind: 'text',
            answer: '',
          },
        ],
      },
    ],
  },
  {
    id: 'financial-assessment',
    name: 'Financial assessment',
    description: "Income, savings and home to find the funding she's due.",
    icon: 'file-lines',
    status: 'Submitted',
    owner: 'Sophie',
    updated: '7 Jun 2026',
    sections: [
      {
        title: 'About Margarette',
        questions: [
          {
            id: 'dob',
            question: 'Date of birth',
            kind: 'text',
            answer: '14 March 1962',
          },
          {
            id: 'postcode',
            question: 'Postcode',
            kind: 'text',
            answer: 'EH3 6HR',
          },
        ],
      },
      {
        title: "Margarette's home",
        questions: [
          {
            id: 'owns',
            question: 'Does she own her home?',
            kind: 'choice',
            answer: 'outright',
            options: [
              { value: 'outright', label: 'Owns it outright' },
              { value: 'joint', label: 'Owns jointly' },
              { value: 'rents', label: 'Rents / lives with family' },
            ],
          },
          {
            id: 'homeValue',
            question: 'Estimated home value',
            kind: 'currency',
            answer: '320,000',
          },
        ],
      },
      {
        title: 'Income & savings',
        questions: [
          {
            id: 'savings',
            question: 'Total savings',
            kind: 'currency',
            answer: '18,000',
          },
          {
            id: 'statePension',
            question: 'State Pension income (a year)',
            kind: 'currency',
            answer: '11,500',
          },
        ],
      },
      {
        title: 'Benefits',
        questions: [
          {
            id: 'attendance',
            question: 'Is she getting Attendance Allowance?',
            kind: 'choice',
            answer: 'lower',
            options: [
              { value: 'higher', label: 'Yes, higher rate' },
              { value: 'lower', label: 'Yes, lower rate' },
              { value: 'no', label: 'No' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'benefits-eligibility',
    name: 'Benefits eligibility',
    lead: true,
    description: 'A few questions to check which benefits she qualifies for.',
    icon: 'shield-check',
    status: 'Not started',
    sections: [
      {
        title: 'Circumstances',
        questions: [
          {
            id: 'pensionAge',
            question: 'Is she over State Pension age?',
            kind: 'yesno',
            answer: '',
            options: YESNO,
          },
          {
            id: 'livesAlone',
            question: 'Does she live alone?',
            kind: 'yesno',
            answer: '',
            options: YESNO,
          },
        ],
      },
      {
        title: 'Current benefits',
        questions: [
          {
            id: 'current',
            question: 'Which benefits does she already receive?',
            kind: 'text',
            answer: '',
          },
        ],
      },
    ],
  },
  {
    id: 'attendance-allowance',
    name: 'Attendance allowance',
    description: 'Help with the extra costs of her care needs.',
    icon: 'file-lines',
    status: 'Not started',
    // The real form, read from the schema rather than written out here. See
    // aaSections.
    sections: aaSections(),
  },
];

// Total answered / total questions across a form's sections. An answer counts
// once it's non-empty.
export const formProgress = (form: FormItem) => {
  const questions = form.sections.flatMap((s) => s.questions);
  return {
    answered: questions.filter((q) => q.answer.trim() !== '').length,
    total: questions.length,
  };
};

// First name for compact rows ('Sophie Qualley' → 'Sophie').
export const firstName = (name: string) => name.split(' ')[0];

// '12 Jun 2026' style date for freshly created items.
export const todayLabel = () =>
  new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

// 'Sophie Qualley' → 'SQ' for avatars.
export const initials = (name: string) =>
  name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
